import React, { useEffect, useState } from 'react';
import { PhoneCall, MessageSquare, Mail, AlertTriangle, Clock } from 'lucide-react';

/**
 * AccountabilityPanel — rep-accountability rollup for one contact (Lead
 * Drill-Down PR-B). Rendered inside ContactDetailView, so it appears in BOTH the
 * slide-over drawer and the full profile with no duplication. Backed by
 * crm:contacts:accountability (all math server-side; gated crm:read). The stale
 * badge tier comes from the shared 7/14/30 thresholds the stale-lead scan uses.
 */
const TIER = {
  warn:     { label: 'Stale 7d+',  bg: '#fef9c3', color: '#854d0e', border: '#fde68a' },
  stale:    { label: 'Stale 14d+', bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  critical: { label: 'Stale 30d+', bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
};

function fmt(ts) {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleDateString();
}

export default function AccountabilityPanel({ contactId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contactId || !window.zignagenixAPI || typeof window.zignagenixAPI.getContactAccountability !== 'function') return;
    let alive = true;
    window.zignagenixAPI.getContactAccountability(contactId)
      .then((r) => { if (!alive) return; if (r && r.error) setError(r.message || 'Failed to load'); else setData(r); })
      .catch(() => { if (alive) setError('Failed to load accountability'); });
    return () => { alive = false; };
  }, [contactId]);

  if (error) return <div style={{ ...wrap, color: '#991b1b' }} role="alert">Accountability: {error}</div>;
  if (!data) return <div style={{ ...wrap, color: '#6C757D' }} aria-busy="true">Loading accountability…</div>;

  const tier = data.staleTier ? TIER[data.staleTier] : null;
  const overdue = Number(data.overdueTasks) || 0;

  return (
    <div style={wrap} aria-label="Rep accountability">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Rep Accountability</span>
        {tier && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: tier.bg, color: tier.color, border: `1px solid ${tier.border}` }}>
            {tier.label}
          </span>
        )}
        <span style={{ fontSize: 11, color: '#64748b' }}>
          {data.daysSinceActivity == null ? 'no activity recorded' : `${data.daysSinceActivity}d since any activity`}
        </span>
      </div>
      <div style={grid}>
        <Stat icon={<PhoneCall size={13} color="#2E8B98" />} label="Last call (rep)" value={fmt(data.lastCallByRep)} />
        <Stat icon={<MessageSquare size={13} color="#2E8B98" />} label="Last text (rep)" value={fmt(data.lastTextByRep)} />
        <Stat icon={<Mail size={13} color="#2E8B98" />} label="Last email (rep)" value={fmt(data.lastEmailByRep)} />
        <Stat icon={<Clock size={13} color="#2E8B98" />} label="Next due" value={fmt(data.nextDueAt)} />
        <Stat icon={<AlertTriangle size={13} color={overdue ? '#b45309' : '#94a3b8'} />} label="Overdue tasks"
          value={String(overdue)} emphasize={overdue > 0} />
        <Stat icon={<AlertTriangle size={13} color={data.missedFollowups ? '#b45309' : '#94a3b8'} />} label="Missed follow-up"
          value={data.missedFollowups ? 'yes' : 'no'} emphasize={!!data.missedFollowups} />
      </div>
    </div>
  );
}

function Stat({ icon, label, value, emphasize }) {
  return (
    <div style={stat}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: '#64748b' }}>{icon}{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: emphasize ? '#b45309' : '#1e293b' }}>{value}</div>
    </div>
  );
}

const wrap = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 13 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 };
const stat = { background: '#fff', border: '1px solid #eef1f5', borderRadius: 6, padding: '6px 8px' };
